import React from 'react'
import { MapPin, User, Phone, Info, Edit2, Trash2 } from 'lucide-react'
import { CustomersAddressEntity } from '@/entities/api/customers/customer'

interface DirectionCardProps {
	customerId: string
	address: CustomersAddressEntity
	token: string | null
	onEdit?: () => void
	onDelete?: () => void
}

const DirectionCard: React.FC<DirectionCardProps> = ({
	address,
	onEdit,
	onDelete,
}) => {

	const formatAddress = () => {
		const parts = [
			address.street_type,
			address.street,
			address.number_home,
		].filter(Boolean)

		return parts.join(' ')
	}



	const formatLocation = () => {
		return `${address.neighborhood}, ${address.city}, ${address.province}`
	}

	const formatDocumentInfo = () => {
		return `${address.document_type} ${address.document.toLocaleString()}`
	}

	const formatPhone = () => {
		const phone = address.phone.toString()
		if (phone.length === 10) {
			return `(${phone.slice(0, 3)}) ${phone.slice(3, 6)}-${phone.slice(6)}`
		}
		return phone
	}

	return (
		<div
			className={`
				relative bg-white border transition-all duration-200 ease-out
		
					
				
						border-gray-200 hover:border-red-200 hover:shadow-md
				rounded-lg p-4 group cursor-pointer
			`}
		>
			{(onEdit || onDelete) && (
				<div className='absolute top-3 right-3 z-10'>
					<div className='flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200'>
						{onEdit && (
							<button
								onClick={(e) => {
									e.stopPropagation()
									onEdit()
								}}
								className='p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200'
								aria-label='Editar dirección'
							>
								<Edit2 className='w-4 h-4' />
							</button>
						)}
						{onDelete && (
							<button
								onClick={(e) => {
									e.stopPropagation()
									onDelete()
								}}
								className='p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors duration-200'
								aria-label='Eliminar dirección'
							>
								<Trash2 className='w-4 h-4' />
							</button>
						)}
					</div>
				</div>
			)}

			<div className='space-y-3'>
				<div className='pr-12'>
					<div className='flex items-start gap-3'>
						<div className='flex-shrink-0 w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mt-0.5'>
							<MapPin className='w-4 h-4 text-red-600' />
						</div>
						<div className='flex-1 min-w-0'>
							<h3 className='text-base font-semibold text-gray-900 leading-tight mb-1'>
								{formatAddress()}
							</h3>
							<p className='text-sm text-gray-600 mb-2 leading-tight'>
								{formatLocation()}
							</p>
							{address.postal_code && (
								<span className='inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-gray-100 text-gray-700'>
									CP {address.postal_code}
								</span>
							)}
						</div>
					</div>
				</div>

				<div className='border-t border-gray-100 pt-3'>
					<div className='space-y-3'>
						<div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
							<div className='flex items-center gap-2.5'>
								<div className='flex-shrink-0 w-6 h-6 flex items-center justify-center'>
									<User className='w-3.5 h-3.5 text-gray-400' />
								</div>
								<div className='flex-1 min-w-0'>
									<p className='text-sm font-medium text-gray-900 truncate'>
										{address.fullname}
									</p>
									<p className='text-xs text-gray-500 truncate'>
										{formatDocumentInfo()}
									</p>
								</div>
							</div>

							<div className='flex items-center gap-2.5'>
								<div className='flex-shrink-0 w-6 h-6 flex items-center justify-center'>
									<Phone className='w-3.5 h-3.5 text-gray-400' />
								</div>
								<div className='flex-1 min-w-0'>
									<p className='text-sm font-medium text-gray-900'>
										{formatPhone()}
									</p>
									{address.type_home && (
										<p className='text-xs text-gray-500 capitalize'>
											{address.type_home}
										</p>
									)}
								</div>
							</div>
						</div>

						{address.additional_references && (
							<div className='flex items-start gap-2.5'>
								<div className='flex-shrink-0 w-6 h-6 flex items-center justify-center mt-0.5'>
									<Info className='w-3.5 h-3.5 text-gray-400' />
								</div>
								<div className='flex-1'>
									<p className='text-xs font-medium text-gray-600 mb-1'>
										Referencias adicionales
									</p>
									<p className='text-xs text-gray-600 bg-gray-50 rounded-md px-2.5 py-1.5 leading-relaxed'>
										{address.additional_references}
									</p>
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

export default DirectionCard
